import React from 'react';
import { FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { BaseField } from './BaseField';
import type { BaseFieldProps } from './types';

/**
 * Dropdown field component that handles:
 * - dropdown (form-builder)
 * - select (legacy)
 */
export const DropdownField: React.FC<BaseFieldProps> = ({
  field,
  value = '',
  onChange,
  onBlur,
  mode = 'edit',
  error,
  touched,
  sx,
  showLabel = true,
  disabled,
}) => {
  // Extract field properties based on field format
  const label = 'label' in field ? field.label : '';
  const required = 'required' in field ? field.required : false;
  const placeholder = 'placeholder' in field ? field.placeholder : '';
  
  // Extract options based on field format
  let options: Array<{ label: string; value: string }> = [];
  
  if ('options' in field && field.options) {
    options = field.options.map((opt: any) => {
      if (typeof opt === 'string') {
        return { label: opt, value: opt };
      } else if ('label' in opt && 'value' in opt) {
        return { label: opt.label, value: String(opt.value) };
      } else if ('text' in opt) {
        return { label: opt.text, value: String(opt.value || opt.id || opt.text) };
      }
      return { label: String(opt), value: String(opt) };
    });
  }

  // Handle display mode
  if (mode === 'display') {
    const selectedOption = options.find(opt => opt.value === value);
    return (
      <BaseField field={field} mode={mode} sx={sx} showLabel={showLabel}>
        <Typography variant="body2">
          {selectedOption?.label || value || '-'}
        </Typography>
      </BaseField>
    );
  }

  // Handle edit/preview modes
  return (
    <BaseField
      field={field}
      mode={mode}
      error={error}
      touched={touched}
      sx={{ ...sx, mb: 2 }}
      showLabel={false}
      disabled={disabled}
    >
      <FormControl fullWidth variant="outlined" size="small">
        <InputLabel id={`${field.id}-label`}>
          {label}
          {required && '*'}
        </InputLabel>
        <Select
          labelId={`${field.id}-label`}
          id={field.id}
          value={value}
          onChange={onChange ? (e) => onChange(e.target.value) : undefined}
          onBlur={onBlur}
          label={label}
          disabled={disabled || mode === 'preview'}
          error={!!(touched && error)}
        >
          {placeholder && (
            <MenuItem value="" disabled>
              <em>{placeholder}</em>
            </MenuItem>
          )}
          {options.map((option, index) => (
            <MenuItem key={index} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </BaseField>
  );
};